import {
  ErrorHandler,
  NgModule,
  isDevMode,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { RouteReuseStrategy, Router } from '@angular/router';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { ServiceWorkerModule } from '@angular/service-worker';
import * as Sentry from '@sentry/capacitor';
import * as SentryAngular from '@sentry/angular';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { IonicStorageModule } from '@ionic/storage-angular';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import {
  LoggerModule,
  NgxLoggerLevel,
  TOKEN_LOGGER_SERVER_SERVICE,
} from 'ngx-logger';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { environment } from 'src/environments/environment';
import { SharedModule } from './shared.module';
import { AuthGuard } from './services/auth-guard.service';
import { createTranslateLoader } from './services/translate.loader';
import { LoggerServerOTELService } from './services/loger-server-otel.service';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  NgxUiLoaderModule,
  NgxUiLoaderConfig,
  SPINNER,
  PB_DIRECTION,
} from 'ngx-ui-loader';
import { AuthInterceptor } from './core/auth.interceptor';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';

// Sentry providers retained but inactive (init is disabled)
// Can be re-enabled if a new DSN is provided in the future
if (false) {
  Sentry.init(
    {
      dsn: '',

      environment: isDevMode()
        ? 'local'
        : environment.production
        ? 'production'
        : 'testing',

      tracesSampleRate: 1.0,

      integrations: [Sentry.browserTracingIntegration()],
    },
    SentryAngular.init
  );
}

const ngxUiLoaderConfig: NgxUiLoaderConfig = {
  fgsType: SPINNER.wanderingCubes,
  fgsSize: 80,
  fgsColor: '#6200ee',
  pbColor: '#6200ee',
  pbDirection: PB_DIRECTION.leftToRight,
  pbThickness: 3,
};

// NgModule
@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    IonicModule.forRoot(),
    AppRoutingModule,
    BrowserAnimationsModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      //enabled: true,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    IonicStorageModule.forRoot(),
    SharedModule,
    TranslateModule.forRoot({
      defaultLanguage: 'es',
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),
    // Logger
    LoggerModule.forRoot({
      level: NgxLoggerLevel.DEBUG,
      serverLogLevel: NgxLoggerLevel.DEBUG,
      serverLoggingUrl: 'https://splunk-hec.ftsdn.com/services/collector/event',
      disableConsoleLogging: true,
    }),
    NgxUiLoaderModule.forRoot(ngxUiLoaderConfig),
  ],
  providers: [
    // ngx-logger
    {
      provide: TOKEN_LOGGER_SERVER_SERVICE,
      useClass: LoggerServerOTELService,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useValue: SentryAngular.createErrorHandler({
        showDialog: true,
      }),
    },
    {
      provide: SentryAngular.TraceService,
      deps: [Router],
    },
    // El unico efecto util es forzar la instanciacion de TraceService via DI:
    // la factory original era () => () => {}, es decir, ignoraba sus deps y
    // devolvia un noop.
    provideAppInitializer(() => {
      inject(SentryAngular.TraceService);
    }),
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    AuthGuard,
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimationsAsync(),
  ],
})
export class AppModule {}
